import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabase";
import ProgressBar from "../components/ProgressBar";

export default function Dashboard() {
  const [players, setPlayers] = useState([]);
  const [donations, setDonations] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    const [playersRes, donationsRes, settingsRes] = await Promise.all([
      supabase.from("players").select("*").order("jersey_number"),
      supabase.from("donations").select("*"),
      supabase.from("coach_settings").select("*").limit(1).single(),
    ]);

    if (playersRes.data) setPlayers(playersRes.data);
    if (donationsRes.data) setDonations(donationsRes.data);
    if (settingsRes.data) setSettings(settingsRes.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const teamGoal = settings?.team_goal || 5000;
  const totalRaised = donations.reduce((s, d) => s + Number(d.amount), 0);
  const totalDonors = donations.length;
  const pct = teamGoal > 0 ? Math.round((totalRaised / teamGoal) * 100) : 0;

  // Per-player raised
  const raisedByPlayer = {};
  donations.forEach((d) => {
    if (d.player_id) {
      raisedByPlayer[d.player_id] = (raisedByPlayer[d.player_id] || 0) + Number(d.amount);
    }
  });

  const exportCSV = () => {
    const header = "Player,Jersey #,Goal,Raised,% Complete\n";
    const rows = players
      .map((p) => {
        const raised = raisedByPlayer[p.id] || 0;
        const pctComplete = p.goal_amount > 0 ? Math.round((raised / p.goal_amount) * 100) : 0;
        return `"${p.name}",${p.jersey_number},${p.goal_amount},${raised},${pctComplete}%`;
      })
      .join("\n");

    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "griffins-fundraising.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-navy/40">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
      <h1 className="font-display text-2xl font-bold text-navy mb-6">
        Dashboard
      </h1>

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-warm-gray p-5">
          <p className="text-sm text-navy/50 mb-1">Total Raised</p>
          <p className="text-3xl font-bold text-navy">
            ${totalRaised.toLocaleString()}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-warm-gray p-5">
          <p className="text-sm text-navy/50 mb-1">Team Goal</p>
          <p className="text-3xl font-bold text-navy">
            ${teamGoal.toLocaleString()}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-warm-gray p-5">
          <p className="text-sm text-navy/50 mb-1">Total Donors</p>
          <p className="text-3xl font-bold text-navy">{totalDonors}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="bg-white rounded-xl border border-warm-gray p-5 mb-6">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-sm font-semibold text-navy">
            Overall Progress
          </span>
          <span className="text-sm font-semibold text-black">{pct}%</span>
        </div>
        <ProgressBar raised={totalRaised} goal={teamGoal} size="lg" />
      </div>

      {/* Actions */}
      <div className="flex gap-3 mb-6">
        <Link
          to="/coach/players"
          className="bg-navy text-white text-sm font-semibold py-2.5 px-5 rounded-lg hover:bg-navy-light transition-colors"
        >
          Add Player
        </Link>
        <button
          onClick={exportCSV}
          className="border border-warm-gray text-navy text-sm font-semibold py-2.5 px-5 rounded-lg hover:bg-warm-gray transition-colors"
        >
          Export CSV
        </button>
      </div>

      {/* Player breakdown table */}
      <div className="bg-white rounded-xl border border-warm-gray overflow-hidden">
        <div className="px-5 py-3 border-b border-warm-gray">
          <h2 className="font-semibold text-navy text-sm">
            Player Breakdown
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-warm-gray bg-cream/50">
                <th className="text-left px-5 py-2.5 font-semibold text-navy/60">
                  Player
                </th>
                <th className="text-left px-5 py-2.5 font-semibold text-navy/60">
                  #
                </th>
                <th className="text-right px-5 py-2.5 font-semibold text-navy/60">
                  Goal
                </th>
                <th className="text-right px-5 py-2.5 font-semibold text-navy/60">
                  Raised
                </th>
                <th className="text-right px-5 py-2.5 font-semibold text-navy/60">
                  %
                </th>
                <th className="text-center px-5 py-2.5 font-semibold text-navy/60">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {players.map((p) => {
                const raised = raisedByPlayer[p.id] || 0;
                const playerPct =
                  p.goal_amount > 0
                    ? Math.round((raised / p.goal_amount) * 100)
                    : 0;
                return (
                  <tr
                    key={p.id}
                    className="border-b border-warm-gray last:border-0 hover:bg-cream/30"
                  >
                    <td className="px-5 py-3 font-medium text-navy">
                      {p.name}
                    </td>
                    <td className="px-5 py-3 text-navy/50">{p.jersey_number}</td>
                    <td className="px-5 py-3 text-right text-navy/60">
                      ${p.goal_amount}
                    </td>
                    <td className="px-5 py-3 text-right font-semibold text-navy">
                      ${raised}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <span
                        className={`font-semibold ${
                          playerPct >= 100
                            ? "text-green-600"
                            : playerPct >= 50
                            ? "text-black"
                            : "text-navy/50"
                        }`}
                      >
                        {playerPct}%
                      </span>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          p.is_active
                            ? "bg-green-100 text-green-700"
                            : "bg-warm-gray text-navy/40"
                        }`}
                      >
                        {p.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {players.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-navy/40">
                    No players yet. Add your first player!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
